import React from 'react';
import { Table } from 'react-bootstrap';

/**
 * ResponsiveTable Component
 *
 * Provides a responsive table that switches between:
 * - Desktop (≥768px): Standard Bootstrap table layout
 * - Mobile (<768px): Card-based layout with labeled fields
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Array of column definitions { key, label, render }
 * @param {Function} props.renderRow - Function(row, index) that returns JSX for mobile card layout
 * @param {boolean} [props.hover=true] - Enable hover effect on table rows
 * @param {boolean} [props.striped=false] - Enable striped rows
 * @param {boolean} [props.responsive=true] - Enable responsive wrapper for table
 * @param {JSX.Element} [props.emptyState=null] - Component to show when data is empty
 * @param {Function} [props.keyExtractor] - Function(row, index) to extract unique key
 */
const ResponsiveTable = ({
  data = [],
  columns = [],
  renderRow,
  hover = true,
  striped = false,
  responsive = true,
  emptyState = null,
  keyExtractor = (row, index) => row.id || index,
}) => {
  // Handle empty state
  if (!data || data.length === 0) {
    return emptyState || null;
  }

  return (
    <div className="responsive-table-wrapper">
      {/* Desktop Table View */}
      <Table responsive={responsive} hover={hover} striped={striped}>
        <thead>
          <tr>
            {columns.map((column, idx) => (
              <th key={column.key || idx}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={keyExtractor(row, index)}>
              {columns.map((column, colIdx) => (
                <td key={`${keyExtractor(row, index)}-${column.key || colIdx}`}>
                  {column.render ? column.render(row[column.key], row, index) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Mobile Card View */}
      <div className="table-cards">
        {data.map((row, index) => (
          <div key={keyExtractor(row, index)} className="table-card">
            {renderRow(row, index)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResponsiveTable;
